import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Phone, Mail, MapPin, Clock, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    course: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCourseChange = (value) => {
    setFormData(prev => ({ ...prev, course: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Mock submission - will be replaced with backend API call
    console.log('Contact Form Submitted:', formData);
    
    // Store in localStorage temporarily (mock)
    const existingContacts = JSON.parse(localStorage.getItem('contacts') || '[]');
    existingContacts.push({
      ...formData,
      id: Date.now(),
      type: 'contact',
      submittedAt: new Date().toISOString()
    });
    localStorage.setItem('contacts', JSON.stringify(existingContacts));
    
    toast.success('Thank you! We will get back to you soon.');
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      course: '',
      message: ''
    });
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1e3a8a] to-[#2563eb] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Get In Touch
            </h1>
            <p className="text-lg md:text-xl text-gray-100">
              Ready to start your international dental career? Contact us today
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-5 gap-6">
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-[#1e3a8a]/10 rounded-full mb-4">
                  <Phone className="h-6 w-6 text-[#1e3a8a]" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Phone</h3>
                <a href="tel:+919914637377" className="text-[#1e3a8a] hover:underline">
                  +91 9914637377
                </a>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-[#25D366]/10 rounded-full mb-4">
                  <MessageCircle className="h-6 w-6 text-[#25D366]" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">WhatsApp</h3>
                <a 
                  href="https://wa.me/919914637377" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#25D366] hover:underline"
                >
                  Chat Now
                </a>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-[#1e3a8a]/10 rounded-full mb-4">
                  <Mail className="h-6 w-6 text-[#1e3a8a]" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Email</h3>
                <a href="mailto:enquiries@overseasdentalachievers.co.in" className="text-[#1e3a8a] hover:underline text-sm">
                  enquiries@overseasdentalachievers.co.in
                </a>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-[#1e3a8a]/10 rounded-full mb-4">
                  <MapPin className="h-6 w-6 text-[#1e3a8a]" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Location</h3>
                <p className="text-sm text-gray-600">
                  SCO 224-225, Sector 40D<br />Chandigarh
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-[#1e3a8a]/10 rounded-full mb-4">
                  <Clock className="h-6 w-6 text-[#1e3a8a]" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Hours</h3>
                <p className="text-sm text-gray-600">
                  Mon-Fri: 10 AM - 7 PM<br />Sat: 10 AM - 2 PM
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">Send Us a Message</CardTitle>
                  <CardDescription>
                    Fill out the form below and we'll get back to you as soon as possible
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Dr. John Doe"
                        required
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        required
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+91 9876543210"
                        required
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="course">Course Interested In *</Label>
                      <Select onValueChange={handleCourseChange} value={formData.course} required>
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select a course" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="adc-part-2">ADC Part 2 Practical</SelectItem>
                          <SelectItem value="ndecc">NDECC</SelectItem>
                          <SelectItem value="irish-dental">Irish Dental Council</SelectItem>
                          <SelectItem value="us-bench-test">US Bench Test</SelectItem>
                          <SelectItem value="general">General Inquiry</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="message">Message</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your goals and any questions you have..."
                        rows={5}
                        className="mt-1"
                      />
                    </div>

                    <Button type="submit" className="w-full bg-[#1e3a8a] hover:bg-[#1e3a8a]/90">
                      Send Message
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Map & Additional Info */}
            <div className="space-y-8">
              {/* Google Map Embed */}
              <Card>
                <CardHeader>
                  <CardTitle>Our Location</CardTitle>
                  <CardDescription>
                    SCO 224-225, Sector 40D, Chandigarh, India
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="aspect-video rounded-lg overflow-hidden">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3430.5472469845793!2d76.72844431513097!3d30.70629598164748!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390fef8e3f8e8e8f%3A0x8e8e8e8e8e8e8e8e!2sSector%2040%2C%20Chandigarh!5e0!3m2!1sen!2sin!4v1234567890"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="ODA Location"
                    ></iframe>
                  </div>
                </CardContent>
              </Card>

              {/* Why Visit Us */}
              <Card className="bg-[#1e3a8a] text-white">
                <CardHeader>
                  <CardTitle>Why Visit Us?</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-bold">✓</span>
                      </div>
                      <span className="text-sm">Tour our state-of-the-art training facility</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-bold">✓</span>
                      </div>
                      <span className="text-sm">Meet our experienced faculty</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-bold">✓</span>
                      </div>
                      <span className="text-sm">Discuss your preparation goals in person</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-bold">✓</span>
                      </div>
                      <span className="text-sm">Get personalized course recommendations</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
